/**
 * Product-tour step catalogue + a pure, DOM-free selector.
 *
 * The tour walks the user through EVERY sidebar menu and submenu they can
 * reach — top-level rows AND the children inside collapsible groups. Two
 * separate ordered lists back the two shells (the Company-Admin / Sub-Admin
 * sidebar vs the regular-User sidebar); `selectTourSteps` picks the right one
 * from the signed-in role, then filters each step by a `when(ctx)` predicate
 * resolved from the SAME role / operation / feature signals the sidebar itself
 * uses — so a user only ever sees steps for areas they can actually open.
 *
 * Targets are CSS selectors against stable sidebar anchors: the `href` of each
 * nav link (scoped under `[data-tour="app-sidebar"]`) for leaves, or a
 * `data-tour="…"` hook for the tree / header widgets. The tour engine forces
 * every collapsible group OPEN while it runs (so submenu links are mounted),
 * scrolls each target into view, and — as a final safety net — skips any step
 * whose target is missing (a feature-off row, a grant a Sub Admin lacks).
 */

/** Can this user reach an operation? Mirrors the real gate — Company Admin has
 *  everything; a Sub Admin only their delegated grants; a regular user only the
 *  operations their Work Role carries. Deliberately NOT the permissive global
 *  `userHasOperation` (which short-circuits Sub Admin to true). */
export function canAccessOperation(user, op) {
  if (!user) return false
  if (user.role === 'company_admin') return true
  if (user.role === 'sub_admin') {
    return Array.isArray(user.subAdminOperations)
      ? user.subAdminOperations.includes(op)
      : false
  }
  return Array.isArray(user.operations) ? user.operations.includes(op) : false
}

/** True when the user can reach ANY of the listed operations. Mirrors the
 *  store's `hasAnyOperation` for a regular member — the only role that sees the
 *  Supervision menu (Managers / Team Leaders), which is gated by several ops. */
export function canAccessAnyOperation(user, ops) {
  return Array.isArray(ops) && ops.some((op) => canAccessOperation(user, op))
}

/** A plan/feature flag is ON unless the tenant EXPLICITLY set it false — the
 *  exact rule the sidebars use to hide a nav item (`features?.[flag] === false`). */
export function featureOn(tenant, flag) {
  return tenant?.features?.[flag] !== false
}

/** Company-Admin-only rows (Billing, Refer & Earn, Support tickets, SOP
 *  Integration, AI Provider Keys, Gamification). */
const isCompanyAdmin = (user) => user?.role === 'company_admin'

/** The two admin shells share the AdminLayout sidebar. */
const isAdminPanel = (user) =>
  user?.role === 'company_admin' || user?.role === 'sub_admin'

// The Supervision menu (UserLayout) surfaces for members with any department- or
// company-wide team scope — kept in lock-step with `showSupervisionSection`
// there. A plain Employee holds none of these, so never sees the section.
const SUPERVISION_OPS = [
  'pages.team_overview',
  'members.view_workspace',
  'members.view_department',
  'pages.team_tasks',
  'tasks.view_department',
  'tasks.view_workspace',
  'pages.team_reports',
  'reports.view_department',
  'reports.view_workspace',
]
const canSupervise = (user) =>
  user?.role === 'user' && canAccessAnyOperation(user, SUPERVISION_OPS)

const SB = '[data-tour="app-sidebar"] ' // sidebar scope prefix

/** Build a leaf step targeting a sidebar link by its `href`. */
function leaf(id, href, title, body, when = () => true) {
  return { id, target: `${SB}a[href="${href}"]`, placement: 'right', title, body, when }
}

/** Tag a run of steps with the sidebar dropdown group/section they live in, so
 *  the tour engine opens ONLY that group when it reaches them (accordion). The
 *  key must match the layout's group id: AdminLayout `item.key`
 *  (administration/configuration/process/reports/logs/integrations) or
 *  UserLayout `SidebarSection id` (supervision/reports). Top-level steps carry
 *  no group and keep every dropdown closed. */
function withGroup(group, ...steps) {
  return steps.map((s) => ({ ...s, group }))
}

// ── Shared topbar tail (both shells) ────────────────────────────────────────
const TOPBAR_TAIL = [
  {
    id: 'notifications',
    target: '[data-tour="tour-notifications"]',
    placement: 'bottom',
    title: 'Notifications',
    body: 'Stay informed about task assignments, updates, Goals and other important activity.',
    when: () => true,
  },
  {
    id: 'profile',
    target: '[data-tour="tour-user-menu"]',
    placement: 'bottom',
    title: 'Profile & settings',
    body: 'Manage your profile, preferences and account settings here — and reopen this tour any time under “Take Product Tour”.',
    when: () => true,
  },
]

// ── Regular-User shell (UserLayout), in sidebar order ───────────────────────
const USER_STEPS = [
  {
    id: 'dashboard',
    target: `${SB}nav a[href="/app"]`,
    placement: 'right',
    title: 'Dashboard',
    body: 'Your home base — a quick view of your work, team activity and updates.',
    when: () => true,
  },
  leaf('sops-library', '/app/sops-library', 'SOPs Library',
    'Browse your company’s Standard Operating Procedures and turn them into tasks.',
    ({ tenant }) => featureOn(tenant, 'sop_integration')),
  leaf('files-links', '/app/files-links', 'Files & Links',
    'Shared files and links for your team, organised by Space.'),
  {
    id: 'spaces',
    target: '[data-tour="nav-spaces"]',
    placement: 'right',
    title: 'Spaces',
    body: 'Spaces organise your team and project work — each with its own Projects, Lists and Tasks.',
    when: () => true,
  },
  ...withGroup('supervision',
    {
      id: 'supervision',
      target: '[data-tour="nav-supervision"]',
      placement: 'right',
      title: 'Supervision',
      body: 'Oversee your team from here. The pages below let you monitor their tasks and reports.',
      when: ({ user }) => canSupervise(user),
    },
    leaf('team-overview', '/app/team-overview', 'Team Overview',
      'See your team roster with each member’s workload and progress at a glance.',
      ({ user }) => canSupervise(user)),
    leaf('team-tasks', '/app/team-tasks', 'Team Tasks',
      'Every task across your team, grouped by status — spot blockers and overdue work.',
      ({ user }) => canSupervise(user)),
    leaf('team-reports', '/app/reports/team', 'Team Reports',
      'Review the Daily / Weekly / Monthly reports your team members submit.',
      ({ user }) => canSupervise(user)),
    leaf('report-settings', '/app/report-settings', 'Report Settings',
      'Configure the Task Type and Item Status options your team picks on their reports.',
      ({ user }) => canSupervise(user)),
  ),
  leaf('goals', '/app/goals', 'Goals',
    'Create and track Weekly and Monthly Goals, and share progress with your team.',
    ({ tenant }) => featureOn(tenant, 'weekly_goals')),
  ...withGroup('reports',
    leaf('reports-daily', '/app/reports/daily', 'Daily Report',
      'Submit your daily work report so your manager can follow your progress.'),
    leaf('reports-weekly', '/app/reports/weekly', 'Weekly Report',
      'Summarise your week — what you delivered and what’s next.'),
    leaf('reports-monthly', '/app/reports/monthly', 'Monthly Report',
      'Your month at a glance — accomplishments and hours logged.'),
  ),
  leaf('leaderboard', '/app/leaderboard', 'Leaderboard',
    'See how points and completed work rank across the team.',
    ({ tenant }) => featureOn(tenant, 'gamification')),
  // Both sidebar rows existed but had no tour step, so the tour walked straight
  // past them and no info popup ever appeared (defect DF_003 / Mantis 22077).
  leaf('our-products', '/app/our-products', 'Our Products',
    'Everything else we build — browse the product range and what each one does.'),
  leaf('user-manual', '/app/user-manual', 'User Manual',
    'The full how-to for your role: every screen and action, explained step by step.'),
  {
    id: 'focus-board',
    target: '[data-tour="nav-tasks"]',
    placement: 'bottom',
    title: 'Focus Board',
    body: 'Your personal task board — always one click away in the header.',
    when: () => true,
  },
]

// ── Admin shell (AdminLayout — Company Admin / Sub Admin), in sidebar order ──
const ADMIN_STEPS = [
  {
    id: 'dashboard',
    target: `${SB}nav a[href="/dashboard"]`,
    placement: 'right',
    title: 'Dashboard',
    body: 'Your admin home — org-wide activity, setup reminders and key numbers.',
    when: () => true,
  },
  // Administration group
  ...withGroup('administration',
    leaf('departments', '/departments', 'Departments',
      'Create the departments your company is structured into.',
      ({ user }) => canAccessOperation(user, 'departments.manage')),
    leaf('work-roles', '/work-roles', 'Work Roles',
      'Define operations-driven Work Roles and what each one can access.',
      ({ user }) => canAccessOperation(user, 'departments.manage')),
    leaf('members', '/members', 'Members',
      'Invite people, place them in departments and Work Roles, and control their access.',
      ({ user }) => canAccessOperation(user, 'members.manage')),
    leaf('external-collaborators', '/external-collaborators', 'External Collaborators',
      'Grant scoped access to people outside your company.',
      ({ user, tenant }) =>
        canAccessOperation(user, 'members.manage') && featureOn(tenant, 'external_collaboration')),
  ),
  {
    id: 'spaces',
    target: '[data-tour="nav-spaces"]',
    placement: 'right',
    title: 'Spaces',
    body: 'The workspace → Space tree. Spaces hold your team’s Projects, Lists and Tasks.',
    when: ({ user }) => canAccessOperation(user, 'workspaces.manage'),
  },
  // Configuration group
  ...withGroup('configuration',
    leaf('workflows', '/workflows', 'Workflows',
      'Design the task status flow (e.g. To Do → In Progress → Done) your team follows.',
      ({ user, tenant }) =>
        canAccessOperation(user, 'workflows.manage') && featureOn(tenant, 'workflows')),
    leaf('reminders-schedule', '/reminders-schedule', 'Reminders & Schedule',
      'Set task reminder timings and the report cadence for your company.',
      ({ user }) => canAccessOperation(user, 'work_reports.view')),
    leaf('usage-report', '/usage-report', 'Usage Report',
      'Track platform usage across your company.',
      ({ user }) => canAccessOperation(user, 'work_reports.view')),
    leaf('gamification-settings', '/gamification-settings', 'Gamification',
      'Tune the points and rules behind the Leaderboard.',
      ({ user, tenant }) => isCompanyAdmin(user) && featureOn(tenant, 'gamification')),
  ),
  leaf('files-links', '/files-links', 'Files & Links',
    'Your company’s shared file and link library.'),
  // Process group
  ...withGroup('process',
    leaf('sops-library', '/sops-library', 'SOPs Library',
      'Author Standard Operating Procedures and generate tasks from them.',
      ({ user, tenant }) =>
        canAccessOperation(user, 'sops.manage') && featureOn(tenant, 'sop_integration')),
    leaf('task-monitor', '/task-monitor', 'Task Monitor',
      'A live view of every task across the team, with powerful filters.',
      ({ user }) => canAccessOperation(user, 'task_monitor.view')),
    leaf('work-reports', '/work-reports', 'Work Reports',
      'Review the work reports your team members file.',
      ({ user }) => canAccessOperation(user, 'work_reports.view')),
  ),
  leaf('goals', '/app/goals', 'Goals',
    'Plan, assign and track Weekly and Monthly Goals across the team.',
    ({ tenant }) => featureOn(tenant, 'weekly_goals')),
  // Personal Reports group (admins file their own too)
  ...withGroup('reports',
    leaf('reports-daily', '/app/reports/daily', 'Daily Report',
      'File your own daily work report, just like your team does.'),
    leaf('reports-weekly', '/app/reports/weekly', 'Weekly Report',
      'Summarise your week.'),
    leaf('reports-monthly', '/app/reports/monthly', 'Monthly Report',
      'Your month at a glance.'),
  ),
  leaf('leaderboard', '/leaderboard', 'Leaderboard',
    'The same standings your team sees — monitor engagement from here.',
    ({ tenant }) => featureOn(tenant, 'gamification')),
  leaf('ai-usage', '/ai-usage', 'AI Usage',
    'This month’s AI token spend for your company.',
    ({ user, tenant }) =>
      canAccessOperation(user, 'ai_usage.view') && featureOn(tenant, 'ai_usage')),
  // Same two rows as the user shell — present in the sidebar, missing from the
  // tour (defect DF_003 / Mantis 22077).
  leaf('our-products', '/app/our-products', 'Our Products',
    'Everything else we build — browse the product range and what each one does.'),
  leaf('user-manual', '/app/user-manual', 'User Manual',
    'The full how-to for your role: every screen and action, explained step by step.'),
  // Logs group
  ...withGroup('logs',
    leaf('audit-log', '/audit-log', 'Audit Log',
      'A tamper-evident history of important changes across the org.',
      ({ user }) => canAccessOperation(user, 'audit_log.view')),
    leaf('login-activity', '/login-activity', 'Login Activity',
      'Who logged in and when, with online status per user.',
      ({ user }) => canAccessOperation(user, 'audit_log.view')),
    leaf('email-logs', '/email-logs', 'Email Status Logs',
      'Every outbound email with delivery status and one-click retry.',
      ({ user }) => canAccessOperation(user, 'audit_log.view')),
  ),
  // Integrations group
  ...withGroup('integrations',
    leaf('sop-integration', '/sop-integration', 'SOP Integration',
      'Connect the ProcessPro SOP engine to your workspace.',
      ({ user, tenant }) => isCompanyAdmin(user) && featureOn(tenant, 'sop_integration')),
    leaf('api-keys', '/api-keys', 'AI Provider Keys',
      'Bring your own AI provider keys for AI features.',
      ({ user, tenant }) => isCompanyAdmin(user) && featureOn(tenant, 'api_access')),
  ),
]

/**
 * The full ordered catalogue (intro + both shells + shared topbar), exported so
 * the unit test can introspect ids. `selectTourSteps` is what the engine uses.
 */
export const TOUR_STEPS = [...USER_STEPS, ...ADMIN_STEPS, ...TOPBAR_TAIL]

/**
 * The subset of steps this user is entitled to, in sidebar order. Picks the
 * shell (admin vs user) from the role so the two step sets never mix, then
 * filters by each step's `when` predicate. Pure + synchronous so it can be
 * unit-tested without a DOM. A throwing predicate fails closed (the step is
 * dropped) rather than break the tour.
 *
 * @param {{ user?: object, tenant?: object }} ctx
 * @returns {Array<object>}
 */
export function selectTourSteps(ctx) {
  const safeCtx = ctx || {}
  const shell = isAdminPanel(safeCtx.user) ? ADMIN_STEPS : USER_STEPS
  const ordered = [...shell, ...TOPBAR_TAIL]
  return ordered.filter((step) => {
    if (!step.when) return true
    try {
      return Boolean(step.when(safeCtx))
    } catch {
      return false
    }
  })
}
